import { DataGrid } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ChartBindingValue,
  CustomChartGroupedTablePayload,
  CustomChartTableColumn,
  CustomChartTableRow,
  CustomChartValueType,
} from '@/entities/dashboards';
import { ChartCardShell } from '../ChartCardShell';
import type { ChartAppearance } from '../chartElements';
import { chartTableHeight, chartTableStyle } from '../chartLayout';
import { buildTableColumns, toIndexedRows } from './chartTable';
import { ChartLegend } from './ChartLegend';
import { valueRuleLegendItems } from './chartLegendItems';
import { useChartTableState } from './useChartTableState';

type Props = {
  /** Диаграмма, чьей таблице принадлежит личный вид: состав колонок, упорядочение и фильтры */
  chartId: string;
  title: string;
  payload: CustomChartGroupedTablePayload;
  /** Тип значений группы: по нему группа-дата показывается датой и упорядочивается по ней же */
  groupType: CustomChartValueType;
  /** Оформление диаграммы: правила раскраски числовых значений по величине */
  appearance?: ChartAppearance;
  /** Высота диаграммы задана пользователем — таблица прокручивает строки внутри неё */
  hasFixedHeight?: boolean;
  /** Выбор строки наполняет связанную диаграмму; не задан — строки не выбираются */
  onSelectElement?: (value: ChartBindingValue) => void;
};

/** Код колонки группы: тем же именем группа приходит колонкой выборки, в строках её нет. */
const GROUP_COLUMN_CODE = 'group';

/**
 * Таблица с группировкой: строки собраны в группы по первому разрезу.
 *
 * Группа проставляется в свою колонку у каждой строки — так таблица остаётся одной, с общими
 * настройкой колонок, упорядочением, фильтрами и прокруткой, как остальные таблицы дашборда.
 */
export const GroupedTableChartView: FC<Props> = ({
  chartId,
  title,
  payload,
  groupType,
  appearance,
  hasFixedHeight,
  onSelectElement,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const tableColumns = useMemo(
    (): CustomChartTableColumn[] => [
      { code: GROUP_COLUMN_CODE, title: t('presets.chart.groupColumn'), type: groupType },
      ...payload.columns,
    ],
    [t, groupType, payload.columns],
  );

  const indexedRows = useMemo(
    () =>
      payload.groups.flatMap((group, groupIndex) => {
        const groupRows: CustomChartTableRow[] = group.rows.map((row) => ({
          ...row,
          [GROUP_COLUMN_CODE]: group.title,
        }));
        return toIndexedRows(groupRows, `${groupIndex}-`);
      }),
    [payload.groups],
  );

  const columns = useMemo(
    () =>
      buildTableColumns(
        tableColumns,
        indexedRows.map((row) => row.values),
        appearance?.valueColorRules,
      ),
    [tableColumns, indexedRows, appearance],
  );

  const legendItems = useMemo(() => valueRuleLegendItems(appearance?.valueColorRules, t), [appearance, t]);

  const { gridProps, onResetColumns, rows, sorting, onSortingChange, filters, onFiltersChange } = useChartTableState({
    chartId,
    columns: tableColumns,
    rows: indexedRows,
  });

  return (
    <ChartCardShell title={title} scrollable={false}>
      <DataGrid
        {...gridProps}
        columns={columns}
        dataSource={{ mode: 'client', data: rows }}
        getRowId={(row) => row.key}
        enableColumnResizing
        enableColumnReordering
        sortMode="single"
        sorting={sorting}
        onSortingChange={onSortingChange}
        filters={filters}
        onFiltersChange={onFiltersChange}
        columnsPanel={{ onReset: onResetColumns }}
        // Выбор ведётся по группе строки: в таблице с группировкой разбирают именно её
        onRowClick={onSelectElement ? (row) => onSelectElement(row.values[GROUP_COLUMN_CODE] ?? null) : undefined}
        height={chartTableHeight(rows.length, hasFixedHeight)}
        style={chartTableStyle(hasFixedHeight)}
      />
      <ChartLegend items={legendItems} />
    </ChartCardShell>
  );
};
